import React, { useState } from "react";
import * as yup from 'yup';
import { Controller, useForm, useFieldArray } from "react-hook-form";
import { connect } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, FormGroup, Label, Input, FormFeedback } from "reactstrap";
import { addBrandFunc as addBrandFuncAction } from "../ReduxToolkit/Reducers/brands";
import { Plus, Trash } from "react-feather";
import { getCamelCaseWord } from "../static/brands_wholesalers_data";
import ExistingProfilesModal from "./ExistingProfilesModal";

const schema = yup.object().shape({
  entries: yup.array().of(
    yup.object().shape({
      name: yup.string().required('Name is required'),
      website: yup.string().required('Website is required'),
    })
  ).min(1, 'At least one entry is required')
});

const AddManuallyModal = ({ type, open, onClose, addBrandFunc, onSuccess }) => {
  // Kept after closing, so the modal's text doesn't change while it fades out
  const [existing, setExisting] = useState({ open: false, ids: [], createdCount: 0 });
  const { control, reset, handleSubmit, formState: { isSubmitting, errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      entries: [{ name: '', website: '' }]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'entries'
  });

  const onSubmit = async (values) => {
    try {
      const response = await addBrandFunc({
        type,
        data: values.entries
      });

      if (response?.status !== 200) {
        return;
      }

      const { created = [], existed = [] } = response.results || {};
      if (existed.length) {
        setExisting({ open: true, ids: [...new Set(existed)], createdCount: created.length });
      }

      onSuccess();
      onClose();
      reset();
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <>
    <ExistingProfilesModal
      type={type}
      open={existing.open}
      ids={existing.ids}
      createdCount={existing.createdCount}
      onClose={() => setExisting((prev) => ({ ...prev, open: false }))}
    />
    <Modal isOpen={open} toggle={onClose} centered style={{ maxWidth: "600px" }}>
      <ModalHeader toggle={onClose}>
        <span style={{ fontSize: "18px", fontWeight: "700", textTransform: "uppercase" }}>
          Add {type} Manually
        </span>
      </ModalHeader>
      <ModalBody>
        {fields.map((field, index) => (
          <div key={field.id} className="mb-4 p-3 border rounded">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6>{getCamelCaseWord(type)} {index + 1}</h6>
              {fields.length > 1 && (
                <button type="button" className="btn" onClick={() => remove(index)}>
                  <Trash size={18} />
                </button>
              )}
            </div>
            <div className="row">
              <div className="col-md-6">
                <FormGroup>
                  <Label>Name *</Label>
                  <Controller
                    name={`entries.${index}.name`}
                    control={control}
                    render={({ field }) => (
                      <Input invalid={!!errors?.entries?.[index]?.name} {...field} />
                    )}
                  />
                  {errors?.entries?.[index]?.name && (
                    <FormFeedback>{errors.entries[index].name.message}</FormFeedback>
                  )}
                </FormGroup>
              </div>
              <div className="col-md-6">
                <FormGroup>
                  <Label>Website *</Label>
                  <Controller
                    name={`entries.${index}.website`}
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="url"
                        placeholder="https://example.com"
                        invalid={!!errors?.entries?.[index]?.website}
                        {...field}
                      />
                    )}
                  />
                  {errors?.entries?.[index]?.website && (
                    <FormFeedback>{errors.entries[index].website.message}</FormFeedback>
                  )}
                </FormGroup>
              </div>
            </div>
          </div>
        ))}

        <div className="mb-3">
          <button
            type="button"
            className="btn d-flex gap-2 align-items-center"
            color="secondary"
            onClick={() => append({ name: '', website: '' })}
          >
            <Plus size={20} />
            Add Another
          </button>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          className="rounded-pill"
          onClick={handleSubmit(onSubmit)}
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <Spinner size="sm" color="light" />
          ) : (
            "Add +"
          )}
        </Button>
      </ModalFooter>
    </Modal>
    </>
  );
};

const mapStateToProps = (state) => ({
  company: state.layout.company,
});

const mapDispatchToProps = {
  addBrandFunc: addBrandFuncAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddManuallyModal);
